import { Component, ComponentInterface, Element, h, Prop } from '@stencil/core';
import isEmpty from 'lodash/isEmpty';
import Papa from 'papaparse';
import axios from 'axios';
import { ChartInputDataFormatType, ChartType } from '../_types/chart-type';
import { ChartStackingMode } from '../_types/chart-stacking-mode';
import { removeInlineStylesFromHead } from '../_helpers/content-security-policy';
import {
	startObserving,
	stopObserving,
} from '../_helpers/moveInlineStylesToHeadObserver';

const Labels = {
	backToTop: 'Back to top',
	longDescription: 'Chart long description',
	checkbox: 'Enable Chart Pattern',
	target: 'Target',
	value: 'Value',
};

let observer;

@Component({
	tag: 'ds-chart-container',
})
export class ChartContainer implements ComponentInterface {
	@Element() el;

	@Prop({ mutable: true }) pattern = false;
	@Prop({ mutable: true }) longDescriptionHidden = true;

	@Prop() csvData = ''; // csvData is for convenience for storybook knob and chromatic (should avoid using it)
	@Prop() csvFile = '';
	@Prop() type: ChartType;
	/**
	 * The default for this property is set to Comma - which denotes the UK and USA number format
	 * Which is: comma as a thousands separator and dot(period) as a decimal separator
	 * e.g: 1,234.56
	 */
	@Prop() inputDataFormatType: ChartInputDataFormatType =
		ChartInputDataFormatType.Comma;
	@Prop() legendPosition: string;
	@Prop() longDescription: string;
	@Prop() yAxisTitle: string;
	@Prop() yAxisMaxValue: number | null = null;
	@Prop() xAxisTitle: string;
	@Prop() xAxisCategories: string;
	@Prop() data: string;
	@Prop() stacking: ChartStackingMode;
	@Prop() plotBands: string;
	@Prop() rangeNames: string;
	@Prop() decimalsSep: string;
	@Prop() thousandsSep: string;
	@Prop() bgColor: string;
	@Prop() isCalledFromCard: boolean;
	@Prop({ mutable: true }) backToTopId = '';
	@Prop({ mutable: true }) backToTopLabel;
	@Prop({ mutable: true }) longDescriptionLabel;
	@Prop({ mutable: true }) checkboxLabel;
	@Prop({ mutable: true }) targetLabel; // bullet chart accessibility table
	@Prop({ mutable: true }) valueLabel; // bullet chart accessibility table
	@Prop() numberOfDecimals = '';
	@Prop() noDataLabelEnabled: boolean;
	@Prop() chartContainerIndex: number;

	showCheckbox = true;
	isBulletChart = false;

	componentWillLoad(): void {
		if (this.csvFile !== '' && typeof this.csvFile === 'string') {
			// get data from csvFile (behaviour on CWP)
			// csvData is for convenience for storybook knob and chromatic (should avoid using it)
			axios
				.get(this.csvFile)
				.then((response) => {
					const containerEl = this.el.querySelector(
						'.ds-chart-accessibility .ds-table-element'
					);
					this.generateChartTable({
						csvText: response.data as string,
						containerEl,
						type: this.type,
						targetLabel: this.targetLabel,
						valueLabel: this.valueLabel,
						xAxisCategories: this.xAxisCategories,
						isCalledFromCard: this.isCalledFromCard,
					});
				})
				.catch(() => {
					console.error(
						`Fail to read CSV file: ${this.csvFile}. Please try again`
					);
				});
		}
		this.backToTopId = `ds-chart-container-back-to-top-${new Date().getTime()}`;

		if (isEmpty(this.checkboxLabel)) {
			this.checkboxLabel = Labels.checkbox;
		}

		if (isEmpty(this.backToTopLabel)) {
			this.backToTopLabel = Labels.backToTop;
		}

		if (isEmpty(this.longDescriptionLabel)) {
			this.longDescriptionLabel = Labels.longDescription;
		}

		if (isEmpty(this.valueLabel)) {
			this.valueLabel = Labels.value;
		}

		if (isEmpty(this.targetLabel)) {
			this.targetLabel = Labels.target;
		}

		this.isBulletChart = ['bulletHorizontal', 'bulletVertical'].includes(
			this.type
		);

		if (window.MutationObserver) {
			startObserving(this.el, observer);
		}
	}

	disconnectedCallback() {
		removeInlineStylesFromHead(this.el.id);
		if (window.MutationObserver) {
			stopObserving(observer);
		}
	}

	generateChartTable = ({
		csvText,
		containerEl,
		type,
		targetLabel,
		valueLabel,
		xAxisCategories,
		isCalledFromCard,
	}: {
		csvText: string;
		containerEl: HTMLElement;
		type: string;
		targetLabel: string;
		valueLabel: string;
		xAxisCategories: string;
		isCalledFromCard: boolean;
	}): void => {
		const results = Papa.parse(csvText);
		const { data = [], meta } = results;

		if (data && data.length > 0 && !meta.aborted) {
			// parsing ok. create table
			const table = document.createElement('table');
			let thead;
			let startIndex = 1;

			const xAxisCategoriesArray = xAxisCategories.split(',');
			const hasXAxisCategories = xAxisCategoriesArray.length > 0;
			const isBulletChart = ['bulletHorizontal', 'bulletVertical'].includes(
				type
			);

			if (isBulletChart) {
				startIndex = 0;
				thead = `<thead><th></th><th>${valueLabel}</th><th>${targetLabel}</th></thead>`;
			} else {
				const labels = data[0];
				const theadCols = ['<th></th>'];
				labels.forEach((label) => {
					theadCols.push(`<th>${label}</th>`);
				});
				thead = `<thead><tr>${theadCols.join('')}</tr></thead>`;
			}

			const tbodyRows = [];

			// Display only first 2 rows for chart card avoiding showing the colors options row
			const displayRows = isCalledFromCard ? 2 : data.length;

			for (let i = startIndex; i < displayRows; i++) {
				const categoryIndex = isBulletChart ? i : i - 1;
				const category = xAxisCategoriesArray[categoryIndex] || categoryIndex;
				const tbodyCols = hasXAxisCategories
					? [`<th>${category}</th>`]
					: ['<th></th>'];
				const values = data[i];
				values.forEach((value) => {
					tbodyCols.push(`<td>${value}</td>`);
				});
				tbodyRows.push(['<tr>', ...tbodyCols, '</tr>'].join(''));
			}

			table.innerHTML = `${thead}${tbodyRows.join('')}`;
			containerEl.appendChild(table);
		}
	};

	toggleLongDescription = () => {
		this.longDescriptionHidden = !this.longDescriptionHidden;
	};

	handleChangePattern = (event: MouseEvent): void => {
		const { checked } = event.target as never;
		this.pattern = checked;
	};

	onClickBackToTop = (event: MouseEvent): void => {
		event.preventDefault();
		event.stopPropagation();
		// focus Chart Long Description button
		this.el.querySelector('.ds-chart-long-description-button').focus();
	};

	getTagName(componentName: string) {
		const parentTag = this.el.tagName.toLocaleLowerCase();
		const prefix = parentTag.split('-')[0];

		return `${prefix}-${componentName}`;
	}

	handleCheckboxChange = (event: CustomEvent) => {
		this.pattern = event.detail.checked;
	};

	render(): void {
		if (
			this.type === 'line' ||
			this.type === 'spline' ||
			this.type === 'scatter'
		) {
			this.showCheckbox = false;
		}

		const shouldShowLegendForRanges =
			this.rangeNames && this.rangeNames.length && this.isBulletChart;

		const downArrowIcon = this.isCalledFromCard
			? 'fa-angle-down'
			: 'fa-angle-double-down';
		const upArrowIcon = this.isCalledFromCard
			? 'fa-angle-up'
			: 'fa-angle-double-up';

		const iconDescription = this.longDescriptionHidden
			? downArrowIcon
			: upArrowIcon;

		const Chart = this.getTagName('chart');
		return (
			<div
				id={`ds-chart-container-${this.chartContainerIndex}`}
				class={`ds-chart-container ds-chart-container-${this.chartContainerIndex}`}
			>
				{this.showCheckbox ? (
					<ds-checkbox
						label={this.checkboxLabel}
						value={this.checkboxLabel}
						onCheckboxChanged={this.handleCheckboxChange}
					></ds-checkbox>
				) : (
					false
				)}

				{shouldShowLegendForRanges ? (
					<div class="ds-chart-bullet-legend">
						{this.rangeNames.split(',').map((range, i) => {
							const trimmedName = range.trim();
							const colorModifier =
								i > 0 ? `ds-chart-bullet-legend-range--${i}` : '';

							return (
								<div class={`ds-chart-bullet-legend-range ${colorModifier}`}>
									{trimmedName}
								</div>
							);
						})}
					</div>
				) : null}

				{/* csv-data is there for storybook knob and chromatic it is not needed in CWP */}
				<Chart
					chartId={`ds-chart-${this.chartContainerIndex}`}
					data-chromatic="ignore"
					type={this.type}
					input-data-format-type={this.inputDataFormatType}
					csv-data={this.csvData}
					csv-file={this.csvFile}
					stacking={this.stacking}
					x-axis-categories={this.xAxisCategories}
					x-axis-title={this.xAxisTitle}
					y-axis-title={this.yAxisTitle}
					y-axis-max-value={this.yAxisMaxValue}
					legend-position={this.legendPosition}
					pattern={this.pattern}
					plot-bands={this.plotBands}
					decimals-sep={this.decimalsSep}
					thousands-sep={this.thousandsSep}
					bg-color={this.bgColor}
					is-called-from-card={this.isCalledFromCard}
					number-of-decimals={this.numberOfDecimals}
					no-data-label-enabled={this.noDataLabelEnabled}
				></Chart>
				<section id={this.backToTopId} class="ds-chart-accessibility">
					<button
						class="ds-body-m-bold ds-chart-long-description-button"
						type="button"
						onClick={this.toggleLongDescription}
					>
						{this.longDescriptionLabel} <i class={`fa ${iconDescription}`}></i>
					</button>
					<div aria-hidden={this.longDescriptionHidden.toString()}>
						{/* 
              <div> must have a class of `highcharts-description` for highchart accessibility module to use it
              https://www.highcharts.com/docs/accessibility/accessibility-module
            */}
						<div
							class="highcharts-description"
							innerHTML={this.longDescription}
						/>
						<div class="ds-table-element" />
						<a
							class="ds-back-top"
							onClick={this.onClickBackToTop}
							href={`#${this.backToTopId}`}
						>
							{this.backToTopLabel} <i class="fas fa-level-up-alt"></i>
						</a>
					</div>
				</section>
			</div>
		);
	}
}
